import { LedTapeConfig } from '@/api/AccessoryApi';
import { ProjectRenderer } from '../ProjectRenderer';
import { HandleManager } from '../managers/HandleManager';
import { MeasurementManager } from '../managers/MeasurementManager';
import { ObjectsManager } from '../managers/ObjectsManager';
import { Post } from '../objects/Post';
import { UserDataObjectType, UserDataProp } from '../types';
import { PostType } from '@/types/creator';

export type LedAvailableSpace = {
	topY: number;
	bottomY: number;
};

type LedTapeAvailableSpaceWithTapesData = LedAvailableSpace & {
	tapesData: {
		length: number;
		posY: number;
	}[];
};

export class HandleBuilder {
	measurementManager: MeasurementManager;

	constructor(
		private renderer: ProjectRenderer,
		private handleManager: HandleManager,
		private objectsManager: ObjectsManager
	) {
		this.measurementManager = new MeasurementManager(renderer);
	}

	build() {
		this.applyHeights();
		this.renderer.objectsManager.handleAutoPositioning();
		this.measurementManager.updateMeasurements();
	}

	buildLedTapes() {
		const ledTape = this.objectsManager.ledsManager.ledTapes[0];
		if (!ledTape) return;

		const upperPost = this.objectsManager.posts.find(
				(post) => post.type === PostType.UPPER
			),
			lowerPost = this.objectsManager.posts.find(
				(post) => post.type === PostType.LOWER
			);
		if (!upperPost || !lowerPost) return;

		const { ledConfig } = ledTape;

		const upperYBound =
				upperPost.collisionBox.min.y - ledConfig.minDistanceToPosts,
			lowerYBound = lowerPost.collisionBox.max.y + ledConfig.minDistanceToPosts;

		const availableSpaces = this.calculateLedTapeAvailableSpaces(
			upperYBound,
			lowerYBound,
			ledConfig
		);

		const ledAvailableSpaces = availableSpaces.reduce<
			LedTapeAvailableSpaceWithTapesData[]
		>((acc, curr) => {
			const spaceLength = Math.abs(curr.topY - curr.bottomY);

			let quantity = 1;

			if (
				Number.isFinite(ledConfig.maxTapeLength) &&
				spaceLength > ledConfig.maxTapeLength
			)
				quantity = Math.ceil(spaceLength / ledConfig.maxTapeLength);

			const totalGaps = (quantity - 1) * ledConfig.gapBetweenTapes;
			const desiredLength = (spaceLength - totalGaps) / quantity;

			acc.push({
				...curr,
				tapesData: Array.from({ length: quantity }, (_, index) => ({
					length: desiredLength,
					posY:
						curr.bottomY +
						index * (desiredLength + ledConfig.gapBetweenTapes) +
						desiredLength / 2,
				})),
			});

			return acc;
		}, []);

		const reducedLedTapesData = ledAvailableSpaces.reduce<
			LedTapeAvailableSpaceWithTapesData['tapesData'][number][]
		>((acc, curr) => {
			acc.push(...curr.tapesData);
			return acc;
		}, []);

		this.objectsManager.ledsManager.setLedTapesQuantity(
			reducedLedTapesData.length
		);

		for (
			let index = 0;
			index < this.objectsManager.ledsManager.ledTapes.length;
			index++
		) {
			const data = reducedLedTapesData[index];
			const letTape = this.objectsManager.ledsManager.ledTapes[index];

			letTape.length = data.length;
			letTape.posY = data.posY;
		}
	}

	private calculateLedTapeAvailableSpaces(
		upperYBound: number,
		lowerYBound: number,
		ledConfig: LedTapeConfig
	) {
		const availableSpaces: LedAvailableSpace[] = [];

		const adjustableAccessories = this.objectsManager.accessories.filter(
			(accessory) => !accessory.hostSlot
		);

		const ranges = [...this.objectsManager.posts, ...adjustableAccessories].map(
			(obj) => ({
				min:
					obj instanceof Post
						? obj.collisionBox.min.y - ledConfig.minDistanceToPosts
						: obj.collisionBox.min.y,
				max:
					obj instanceof Post
						? obj.collisionBox.max.y + ledConfig.minDistanceToPosts
						: obj.collisionBox.max.y,
			})
		);

		ranges.sort((a, b) => a.min - b.min);

		const merged = [];

		for (const range of ranges) {
			const last = merged[merged.length - 1];
			if (!last || range.min > last.max) merged.push({ ...range });
			else last.max = Math.max(last.max, range.max);
		}

		if (merged.length === 0 || merged[0].min > lowerYBound)
			availableSpaces.push({
				topY: merged[0]?.min ?? upperYBound,
				bottomY: lowerYBound,
			});

		for (let i = 0; i < merged.length - 1; i++) {
			const topY = merged[i + 1].min;
			const bottomY = merged[i].max;
			if (topY > bottomY) {
				availableSpaces.push({ topY, bottomY });
			}
		}

		const lastMax = merged[merged.length - 1]?.max;
		if (lastMax !== undefined && lastMax < upperYBound)
			availableSpaces.push({ topY: upperYBound, bottomY: lastMax });

		return availableSpaces;
	}

	private applyHeights() {
		this.applyHandleHeight();
		this.applyEndingsHeights();
	}

	private applyHandleHeight() {
		const originalHeight = this.handleManager.originalHeights.handle,
			targetHeight = this.handleManager.adjustableHeights.handle;

		const scale = targetHeight / originalHeight;

		this.applyScale(scale, UserDataObjectType.MIDDLE_SEGMENT);

		this.handleManager.updateBoxes();
		this.handleManager.setOriginToYCenterConst();
		this.renderer.objectsManager.updateBoxes();
	}

	private applyEndingsHeights() {
		if (
			!this.handleManager.upperEndingAdjustable ||
			!this.handleManager.lowerEndingAdjustable
		)
			return;
		let originalHeight = this.handleManager.originalHeights.upperEnding,
			targetHeight = this.handleManager.adjustableHeights.upperEnding;

		let scale = targetHeight / originalHeight;

		this.applyScale(scale, UserDataObjectType.UPPER_ENDING);

		originalHeight = this.handleManager.originalHeights.lowerEnding;
		targetHeight = this.handleManager.adjustableHeights.lowerEnding;

		scale = targetHeight / originalHeight;
		this.applyScale(scale, UserDataObjectType.LOWER_ENDING);
	}

	private applyScale(scale: number, objectType: UserDataObjectType) {
		this.renderer.scene.traverse((object) => {
			if (object.userData[UserDataProp.OBJECT_TYPE] !== objectType) return;

			object.scale.set(1, scale, 1);
			object.updateMatrixWorld(true);
			object.traverse((child) => {
				if (child.userData[UserDataProp.REVERT_SCALE] !== objectType) return;
				child.scale.set(1, 1 / scale, 1);
				child.updateMatrixWorld(true);
			});
		});
	}
}
